import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button as MuiButton,
  Typography,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  LinearProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  LocationCity as LocationCityIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Dashboard as DashboardIcon,
  Cloud as CloudIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Insights as InsightsIcon,
  TrendingUp as TrendingUpIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useApiClient } from '../hooks/useApiClient';

import { useCityValidation } from '../hooks/useCityValidation';
import WorkflowResultSelector from '../components/WorkflowResultSelector';
import { WorkflowResultOption } from '../hooks/useWorkflowResults';
import { SerpCityValidator } from '../components/SerpCityValidator';
import UnifiedSamplingDialog from '../components/serp/UnifiedSamplingDialog';
import WorkflowStepper from '@/components/WorkflowStepper';
import WorkflowRunHistory from '../components/serp/WorkflowRunHistory';
import { getWorkflowStatus } from '@/utils/moduleStatus';
import { clientService } from '../services/api/clientService';
import { useWorkflowContext } from '../context/WorkflowContext';
import { useDebugLogger } from '../components/DebugPanel';

interface SerpBatchResult {
  batch_id: string;
  total_queries: number;
  max_queries: number;
  processing_time_seconds: number;
  success_count: number;
  failed_count: number;
  blob_container: string;
  blob_prefix: string;
  download_urls: {
    single_file: string;
    zip_file: string;
    summary_report?: string;
  };
  has_domains: boolean;
  tracked_domains?: string[];
  storage_info?: {
    snowflake_save_success?: boolean;
    snowflake_save_error?: string;
  };
}

interface SerpBatchStartResponse {
  batch_id: string;
  status: string;
  total_queries: number;
  max_queries: number;
  message: string;
  created_at: string;
  estimated_completion_time?: string;
  architecture_version?: string;
  blob_first?: boolean;
  snowflake_ingestion?: any;
}

interface SerpBatchStatusResponse {
  batch_id: string;
  status: string;
  created_at: string;
  updated_at: string;
  total_queries: number;
  completed_queries?: number;
  current_query?: string;
  current_location?: string;
  progress_percentage?: number;
  estimated_completion_time?: string;
  processing_time_seconds?: number;
  success_count?: number;
  failed_count?: number;
  blob_container?: string;
  blob_prefix?: string;
  download_urls?: {
    single_file: string;
    zip_file: string;
    summary_report?: string;
  };
  has_domains?: boolean;
  tracked_domains?: string[];
  error?: string;
}

interface RankingSummary {
  metadata: {
    generated_at: string;
    total_queries: number;
    successful_queries: number;
    tracked_domains: string[];
    num_results_per_query: number;
  };
  overview: {
    queries_with_rankings: number;
    total_rankings_found: number;
    average_position?: number;
    best_ranking?: number;
    worst_ranking?: number;
  };
  domain_summary: Record<string, {
    total_appearances: number;
    queries_found: string[];
    positions: number[];
    average_position?: number;
    best_position?: number;
    worst_position?: number;
    result_types: { organic: number; local: number };
  }>;
  query_rankings: Array<{
    query: string;
    timestamp: string;
    domain_rankings: Record<string, {
      position: number;
      title: string;
      link: string;
      result_type: string;
    }>;
    organic_results_count: number;
    local_results_count: number;
    total_results: number;
  }>;
}

const GOOGLE_DOMAINS = [
  { value: 'google.com', label: 'Google.com (US)' },
  { value: 'google.ca', label: 'Google.ca (Canada)' },
  { value: 'google.co.uk', label: 'Google.co.uk (UK)' },
  { value: 'google.com.au', label: 'Google.com.au (Australia)' },
  { value: 'google.de', label: 'Google.de (Germany)' },
  { value: 'google.fr', label: 'Google.fr (France)' },
  { value: 'google.co.in', label: 'Google.co.in (India)' },
  { value: 'google.co.jp', label: 'Google.co.jp (Japan)' },
  { value: 'google.com.br', label: 'Google.com.br (Brazil)' },
  { value: 'google.com.mx', label: 'Google.com.mx (Mexico)' },
];

const RESULT_COUNT_OPTIONS = [
  { value: 10, label: '10 results' },
  { value: 20, label: '20 results' },
  { value: 50, label: '50 results' },
  { value: 100, label: '100 results' },
];

const SerpTool: React.FC = () => {
  const { callApi } = useApiClient();
  const navigate = useNavigate();
  const { clientId, workflowId } = useParams<{ clientId?: string; workflowId?: string }>();
  
  // Determine if we're in workflow context
  const isWorkflowMode = !!(clientId && workflowId);
  
  // Debug logging - module-scoped to 'serp-tool'
  const debugLogger = useDebugLogger('serp-tool');
  const debug = debugLogger.debug as any; // Temporary type assertion until TS server restarts
  
  // Snowflake integration now handled by backend
  
  const {
    validateCities,
    clearValidation,
    isValidating,
    validationResults,
    validationSummary,
    error: validationError
  } = useCityValidation();
  
  // Get WorkflowContext for triggering stepper refresh
  const { triggerRefresh } = useWorkflowContext();
  
  const [keywords, setKeywords] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [domainInput, setDomainInput] = useState('');
  const [googleDomain, setGoogleDomain] = useState('google.com');
  const [numResults, setNumResults] = useState(20);
  const [apiKey, setApiKey] = useState('');
  const [rateLimitMs, setRateLimitMs] = useState(100);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<SerpBatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentQuery, setCurrentQuery] = useState<string>('');
  const [processedCount, setProcessedCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [citiesExpanded, setCitiesExpanded] = useState(false);
  const [domainsExpanded, setDomainsExpanded] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('US'); // Default to US
  const [rankingSummary, setRankingSummary] = useState<RankingSummary | null>(null);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [selectedWorkflowResult, setSelectedWorkflowResult] = useState<WorkflowResultOption | null>(null);
  const [showCityValidator, setShowCityValidator] = useState(false);
  const [clientData, setClientData] = useState<any>(null);
  // V3 Architecture: No need to track Snowflake saves - integration is automatic
  
  // Snowflake sampling states
  const [showSnowflakeSampling, setShowSnowflakeSampling] = useState(false);
  const [samplingStrategy, setSamplingStrategy] = useState('representative');
  const [sampleSize, setSampleSize] = useState(100);
  const [loadingSnowflakeKeywords, setLoadingSnowflakeKeywords] = useState(false);
  const [isStepperCollapsed, setIsStepperCollapsed] = useState(false);
  const [workflowHistoryKey, setWorkflowHistoryKey] = useState(0); // Key to force refresh WorkflowRunHistory
  const [batchId, setBatchId] = useState<string | null>(null); // Current batch ID for resume functionality
  
  // V3 Architecture: Snowflake integration now handled automatically during SERP processing
  // No separate Snowflake ingestion step needed
  
  // Ref to store the current polling interval
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const totalQueries = cities.length === 0 ? keywords.length : keywords.length * cities.length;
  const queryLimitExceeded = totalQueries > 5000;
  
  // Parse numeric client ID for database operations
  const numericClientId = clientId && /^\d+$/.test(clientId) 
    ? parseInt(clientId, 10) 
    : undefined;

  // Handler for stepper collapse change
  const handleStepperCollapseChange = (collapsed: boolean) => {
    setIsStepperCollapsed(collapsed);
  };
  
  // Check for existing SERP batch in workflow mode
  useEffect(() => {
    if (clientId && workflowId) {
      debug.log('Checking for existing SERP batch in workflow mode', {
        workflow_id: workflowId,
        client_id: clientId
      }, 'SerpTool');
      checkForExistingBatch();
    }
    
    // Cleanup function to clear polling interval when component unmounts or dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        debug.log('Cleaning up polling interval', {}, 'SerpTool');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [clientId, workflowId]); // Use actual dependencies instead of derived value

  const checkForExistingBatch = async () => {
    try {
      // Step 1: Get the latest SERP batch for this workflow
      const batchInfo = await callApi(`/api/v1/workflows/${workflowId}/latest-serp-batch`);
      
      if (!batchInfo?.has_batch) {
        // Step 2: No record - treat as first time
        debug.log('No existing SERP batch found for workflow', { workflow_id: workflowId });
        return;
      }
      
      debug.info(`Found existing SERP batch: ${batchInfo.batch_id || 'unknown'} (${batchInfo.status})`, {
        batch_id: batchInfo.batch_id,
        status: batchInfo.status,
        workflow_id: workflowId,
        has_batch: batchInfo.has_batch
      });
      
      // Step 3: If status is processing, show progress bar
      if (batchInfo.status === 'processing') {
        if (!batchInfo.batch_id) {
          debug.warn('Processing batch found but no batch_id available - cannot resume polling', {
            workflow_id: workflowId,
            status: batchInfo.status
          });
          setSnackbarMessage('Processing batch found but batch ID unavailable - please start a new batch');
          return;
        }
        
        setSnackbarMessage(`Resuming SERP batch ${batchInfo.batch_id}...`);
        setBatchId(batchInfo.batch_id);
        setLoading(true);
        startPollingForBatch(batchInfo.batch_id);
        
      // Step 4: If status is completed, load batch result panel  
      } else if (batchInfo.status === 'completed') {
        if (!batchInfo.batch_id) {
          debug.warn('Completed batch found but no batch_id available - cannot load results', {
            workflow_id: workflowId,
            status: batchInfo.status
          });
          setSnackbarMessage('Completed batch found but batch ID unavailable - results cannot be loaded');
          return;
        }
        
        setSnackbarMessage(`Loading previous SERP results...`);
        setBatchId(batchInfo.batch_id);
        loadCompletedBatchResults(batchInfo.batch_id);
        
      } else if (batchInfo.status === 'failed') {
        const displayBatchId = batchInfo.batch_id || 'unknown';
        setSnackbarMessage(`Previous SERP batch failed: ${displayBatchId}`);
        setBatchId(batchInfo.batch_id);
      } else {
        debug.warn(`Unknown batch status: ${batchInfo.status}`, { 
          batch_id: batchInfo.batch_id,
          workflow_id: workflowId
        });
      }
    } catch (error) {
      debug.error('Failed to check for existing SERP batch', { 
        error: error.message, 
        workflow_id: workflowId 
      });
      // Silent fail - just means no previous batch or API unavailable
    }
  };
  
  // Reusable function to start polling for any batch
  const startPollingForBatch = (batchId: string, totalQueries?: number, rateLimitMs?: number) => {
    console.log(`🔄 SERP: Starting polling for batch ${batchId}`);
    
    // Clear any existing polling interval first
    if (pollingIntervalRef.current) {
      console.log('🧹 SERP: Clearing existing polling interval before starting new one');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 15; // Increased tolerance for large batches
    // Dynamic timeout based on batch size (minimum 30 minutes, maximum 6 hours)
    const estimatedProcessingTime = totalQueries ? totalQueries * (rateLimitMs || 100 + 2000) : 0;
    const minTimeout = 30 * 60 * 1000; // 30 minutes minimum
    const maxTimeout = 6 * 60 * 60 * 1000; // 6 hours maximum
    const dynamicTimeout = Math.max(minTimeout, Math.min(maxTimeout, estimatedProcessingTime * 1.5)); // 1.5x buffer
    const maxPollingTime = dynamicTimeout;
    const startTime = Date.now();
    
    // Dynamic polling interval based on batch size
    const getPollingInterval = (totalQueries: number) => {
      if (totalQueries <= 50) return 2000;      // 2 seconds for small batches
      if (totalQueries <= 200) return 5000;     // 5 seconds for medium batches  
      if (totalQueries <= 1000) return 10000;   // 10 seconds for large batches
      return 15000;                             // 15 seconds for very large batches
    };
    
    const pollingInterval = getPollingInterval(totalQueries || 0);
    
    console.log(`🔄 SERP: Polling every ${pollingInterval/1000}s with ${Math.round(maxPollingTime / 60000)}min timeout for ${totalQueries} queries`);
    
    // Calculate estimated duration if provided
    const estimatedDuration = totalQueries && rateLimitMs ? 
      totalQueries * (rateLimitMs + 2000) : 0;

    const statusCheckInterval = setInterval(async () => {
      try {
        const elapsedTime = Date.now() - startTime;
        
        // Check for timeout
        if (elapsedTime > maxPollingTime) {
          clearInterval(statusCheckInterval);
          pollingIntervalRef.current = null;
          setLoading(false);
          setError('Batch processing timeout - please check manually using the batch ID');
          
          if (isWorkflowMode && triggerRefresh) {
            triggerRefresh();
          }
          return;
        }
        
        const statusResponse = await callApi<SerpBatchStatusResponse>(`/api/v1/serp/batch/${batchId}/status`);
        
        if (!statusResponse) {
          console.warn('No status response received, continuing to poll...');
          return;
        }
        
        // Reset error counter on successful response
        consecutiveErrors = 0;
        
        // Update progress based on real data from backend
        if (statusResponse.progress_percentage !== undefined) {
          setProgress(Math.max(1, statusResponse.progress_percentage));
        } else if (estimatedDuration > 0) {
          // Fallback to time-based estimation if no real progress available
          const progressPercent = Math.max(1, Math.min(90, (elapsedTime / estimatedDuration) * 90));
          setProgress(progressPercent);
        }
        
        // Update detailed progress information
        if (statusResponse.current_query) {
          setCurrentQuery(statusResponse.current_query);
        }
        if (statusResponse.completed_queries !== undefined) {
          setProcessedCount(statusResponse.completed_queries);
        }
        if (statusResponse.total_queries !== undefined) {
          setTotalCount(statusResponse.total_queries);
        }

                 if (statusResponse.status === 'completed') {
           clearInterval(statusCheckInterval);
           pollingIntervalRef.current = null;
           setLoading(false);
           
           // Fetch the completed results
           const resultsResponse = await callApi<SerpBatchResult>(`/api/v1/serp/batch/${batchId}/results`);
           
           if (resultsResponse) {
             setProgress(100);
             setResult(resultsResponse);
             
             // Debug logging for blob storage success
             debug.info('SERP batch processing completed successfully', {
               batch_id: resultsResponse.batch_id,
               total_queries: resultsResponse.total_queries,
               success_count: resultsResponse.success_count,
               failed_count: resultsResponse.failed_count,
               processing_time: resultsResponse.processing_time_seconds,
               blob_container: resultsResponse.blob_container,
               blob_prefix: resultsResponse.blob_prefix,
               architecture_version: 'v2',
               blob_storage_success: true
             }, 'SerpTool');

             debug.info(`Results saved to blob storage successfully: ${resultsResponse.success_count}/${resultsResponse.total_queries} queries processed`, {
               container: resultsResponse.blob_container,
               prefix: resultsResponse.blob_prefix,
               success_rate: resultsResponse.total_queries > 0 ? ((resultsResponse.success_count / resultsResponse.total_queries) * 100).toFixed(1) + '%' : 'N/A'
             }, 'SerpTool');
             
             if (resultsResponse.has_domains && resultsResponse.download_urls.summary_report) {
               await fetchSummaryReport(resultsResponse.download_urls.summary_report);
             }
             setSnackbarMessage('Batch processing completed successfully!');
             
             // V3 Architecture: Snowflake integration already completed during SERP processing
             // No separate ingestion step needed
            
             if (isWorkflowMode && triggerRefresh) {
               triggerRefresh();
             }
           } else {
             setError('Failed to fetch completed results');
           }
         } else if (statusResponse.status === 'failed') {
           clearInterval(statusCheckInterval);
           pollingIntervalRef.current = null;
           setLoading(false);
           setError(statusResponse.error || 'Batch processing failed');
           
           if (isWorkflowMode && triggerRefresh) {
             triggerRefresh();
           }
         } else if (statusResponse.status === 'cancelled') {
           clearInterval(statusCheckInterval);
           pollingIntervalRef.current = null;
           setLoading(false);
           setError('Batch processing was cancelled (server may have restarted)');
           
           if (isWorkflowMode && triggerRefresh) {
             triggerRefresh();
           }
         }
        
      } catch (statusError: any) {
        console.error('Error checking batch status:', statusError);
        console.error('Error details:', {
          message: statusError.message,
          status: statusError.status,
          response: statusError.response?.data,
          batchId: batchId
        });
        consecutiveErrors++;
        
        if (consecutiveErrors >= maxConsecutiveErrors) {
          console.error(`Too many consecutive errors (${consecutiveErrors}), stopping polling`);
          clearInterval(statusCheckInterval);
          pollingIntervalRef.current = null;
          setLoading(false);
          setError(`Failed to get status after ${maxConsecutiveErrors} attempts. Last error: ${statusError.message}. Batch ID: ${batchId}`);
          
          if (isWorkflowMode && triggerRefresh) {
            triggerRefresh();
          }
          return;
        }
        
        console.log(`Consecutive errors: ${consecutiveErrors}/${maxConsecutiveErrors} - Will retry in ${pollingInterval/1000}s`);
      }
    }, pollingInterval); // Dynamic polling interval based on batch size
    
    // Store the interval reference for cleanup
    pollingIntervalRef.current = statusCheckInterval;
  };
  
  // Helper function to load completed batch results
  const loadCompletedBatchResults = async (batchId: string) => {
    try {
      console.log(`🔍 SERP: Loading completed batch results for ID: ${batchId}`);
      setLoading(true);
      
      const resultsResponse = await callApi<SerpBatchResult>(`/api/v1/serp/batch/${batchId}/results`);
      
      console.log(`🔍 SERP: Results response:`, resultsResponse);
      
      if (resultsResponse) {
        console.log(`✅ SERP: Setting result data`);
        setResult(resultsResponse);
        setProgress(100);
        
        if (resultsResponse.has_domains && resultsResponse.download_urls.summary_report) {
          console.log(`📊 SERP: Fetching summary report`);
          await fetchSummaryReport(resultsResponse.download_urls.summary_report);
        }
        setSnackbarMessage('Loaded SERP batch results successfully!');
        console.log(`✅ SERP: Batch results loaded successfully`);
      } else {
        console.warn(`⚠️ SERP: No results response received`);
        setError('No results found for this batch');
      }
    } catch (error: any) {
      console.error('❌ SERP: Error loading completed batch results:', error);
      console.error('❌ SERP: Error details:', {
        message: error?.message,
        status: error?.status,
        statusText: error?.statusText,
        data: error?.data
      });
      setError(`Failed to load batch results: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch client data
  useEffect(() => {
    const fetchClientData = async () => {
      if (numericClientId) {
        try {
          const client = await clientService.getClient(numericClientId);
          setClientData(client);
        } catch (error) {
          console.error('Failed to fetch client data:', error);
        }
      }
    };
    
    fetchClientData();
  }, [numericClientId]);

  // Handler for workflow selection
  const handleWorkflowSelect = (workflow: WorkflowResultOption | null) => {
    setSelectedWorkflowResult(workflow);
  };

  // Handler for extracted keywords from workflow
  const handleKeywordsExtracted = (extractedKeywords: string[]) => {
    // Filter out any invalid keywords from extracted keywords
    const validExtractedKeywords = extractedKeywords.filter(keyword => 
      keyword !== null && 
      keyword !== undefined && 
      typeof keyword === 'string' && 
      keyword.trim().length > 0
    );
    
    console.log('Extracted keywords - Original:', extractedKeywords.length, 'Valid:', validExtractedKeywords.length);
    if (extractedKeywords.length !== validExtractedKeywords.length) {
      console.warn('Invalid keywords filtered from extracted data:', 
        extractedKeywords.filter(k => !validExtractedKeywords.includes(k)));
    }
    
    if (validExtractedKeywords.length > 0) {
      // Add extracted keywords to existing keywords, avoiding duplicates
      const newKeywords = validExtractedKeywords.filter(k => !keywords.includes(k));
      if (newKeywords.length > 0) {
        setKeywords(prev => [...prev, ...newKeywords]);
        setSnackbarMessage(`Added ${newKeywords.length} keywords from workflow results (${validExtractedKeywords.length} total extracted)`);
      } else {
        setSnackbarMessage(`${validExtractedKeywords.length} keywords extracted, but all were already present`);
      }
    } else {
      setSnackbarMessage('No valid keywords found in the selected workflow results');
    }
  };

  // V3 Architecture: Snowflake ingestion now handled automatically during SERP processing
  // No separate ingestion function needed

  // Handler for Snowflake sampled keywords
  const handleSnowflakeKeywordsLoaded = (sampledKeywords: string[]) => {
    // Filter out any invalid keywords
    const validKeywords = sampledKeywords.filter(keyword => 
      keyword !== null && 
      keyword !== undefined && 
      typeof keyword === 'string' && 
      keyword.trim().length > 0
    );
    
    console.log('Snowflake keywords - Original:', sampledKeywords.length, 'Valid:', validKeywords.length);
    if (sampledKeywords.length !== validKeywords.length) {
      console.warn('Invalid keywords filtered from Snowflake data:', 
        sampledKeywords.filter(k => !validKeywords.includes(k)));
    }
    
    setKeywords(validKeywords);
    setShowSnowflakeSampling(false);
    
    // Clear previous results when loading new keywords
    setResult(null);
    setRankingSummary(null);
    
    setSnackbarMessage(`Loaded ${validKeywords.length} keywords from Snowflake sampling`);
  };
  
  // Save SERP results to workflow storage
  const saveToWorkflowStorage = async (serpResult: SerpBatchResult) => {
    if (!clientId || !workflowId) return null;
    
    try {
      const response = await callApi('/api/v1/serp/workflow-save', {
        method: 'POST',
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId,
          batch_id: serpResult.batch_id,
          serp_results: serpResult,
          sampling_metadata: samplingStrategy ? {
            strategy: samplingStrategy,
            sample_size: sampleSize
          } : null
        })
      });
      
      if (response && response.success) {
        console.log('SERP results saved to workflow storage:', response.blob_path);
        
        // Return enhanced serpResult with download URLs for Snowflake integration
        return {
          ...serpResult,
          blob_container: response.blob_container,
          blob_prefix: response.blob_prefix,
          download_urls: response.download_urls
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to save SERP results to workflow storage:', error);
      return null;
    }
  };

  // Auto-save to workflow storage and persist keywords when results are ready
  useEffect(() => {
    if (result && result.download_urls.zip_file) {
      
      console.log('SERP results ready, handling post-processing...');
      
      // Save to workflow storage if in workflow mode and update result with workflow data
      const handleWorkflowSave = async () => {
        if (isWorkflowMode && clientId && workflowId) {
          const enhancedResult = await saveToWorkflowStorage(result);
          if (enhancedResult) {
            // Keep original download URLs but update other workflow-related data
            console.log('Preserving original download URLs:', result.download_urls);
            console.log('Workflow storage info:', enhancedResult);
            setResult({
              ...enhancedResult,
              download_urls: result.download_urls // Keep original URLs from serp-results
            });
          }
        }
      };
      
      handleWorkflowSave();
      
      // Persist keywords to localStorage for use in DataForSeoTool
      if (keywords.length > 0) {
        try {
          const keywordData = {
            keywords: keywords,
            timestamp: new Date().toISOString(),
            serpBatchId: result.batch_id,
            cities: cities,
            domains: domains
          };
          
          if (clientId && workflowId) {
            // Store with workflow-specific key
            localStorage.setItem(`workflow_${clientId}_${workflowId}_serp_keywords`, JSON.stringify(keywordData));
            console.log(`Persisted ${keywords.length} keywords for workflow ${clientId}/${workflowId}`);
          } else {
            // Store with general key for non-workflow usage
            localStorage.setItem('serp_keywords_latest', JSON.stringify(keywordData));
            console.log(`Persisted ${keywords.length} keywords from latest SERP analysis`);
          }
        } catch (error) {
          console.error('Error persisting keywords to localStorage:', error);
        }
      }
    }
  }, [result, keywords, cities, domains, clientId, workflowId, isWorkflowMode]);

  // Auto-match high confidence city validations
  useEffect(() => {
    if (validationResults && !isValidating && cities.length > 0) {
      let hasLowConfidenceMatches = false;
      let autoMatchCount = 0;
      
      const updatedCities = cities.map(city => {
        const result = validationResults[city];
        if (result && result.valid && result.confidence && result.confidence >= 85 && result.canonical_name) {
          // Auto-accept high confidence match
          autoMatchCount++;
          return result.canonical_name;
        } else if (result && !result.valid) {
          hasLowConfidenceMatches = true;
        }
        return city;
      });
      
      // Update cities with auto-matched canonical names if any were changed
      if (autoMatchCount > 0) {
        setCities(updatedCities);
        setSnackbarMessage(`Auto-matched ${autoMatchCount} cities with high confidence (90%+)`);
      }
      
      // Only show validator if there are low confidence matches or invalid cities
      if (hasLowConfidenceMatches) {
        setShowCityValidator(true);
      } else if (autoMatchCount === 0 && validationSummary && validationSummary.invalid_cities === 0) {
        setSnackbarMessage(`All ${cities.length} cities validated successfully!`);
      }
    }
  }, [validationResults, isValidating]);

  const handleAddKeywords = () => {
    const newKeywords = keywordInput
      .split('\n')
      .map(k => k.trim())
      .filter(k => k && !keywords.includes(k));
    
    if (newKeywords.length > 0) {
      setKeywords([...keywords, ...newKeywords]);
      setKeywordInput('');
    }
  };

  const handleLoadDataForSeoKeywords = () => {
    try {
      let keywordData = null;
      
      // Try to load workflow-specific keywords first
      if (clientId && workflowId) {
        const workflowKey = `workflow_${clientId}_${workflowId}_dataforseo_keywords`;
        const storedData = localStorage.getItem(workflowKey);
        if (storedData) {
          keywordData = JSON.parse(storedData);
        }
      }
      
      // Fallback to latest DataForSeo keywords if no workflow-specific data found
      if (!keywordData) {
        const latestData = localStorage.getItem('dataforseo_keywords_latest');
        if (latestData) {
          keywordData = JSON.parse(latestData);
        }
      }
      
      // Load the keywords if found
      if (keywordData && keywordData.keywords && keywordData.keywords.length > 0) {
        // Option to replace or add keywords
        const shouldReplace = keywords.length === 0 || 
          window.confirm(`Replace existing ${keywords.length} keywords with ${keywordData.keywords.length} keywords from DataForSeo?\n\nClick 'Cancel' to add to existing keywords instead.`);
        
        // Filter out any invalid keywords from DataForSeo
        const validKeywords = keywordData.keywords.filter(keyword => 
          keyword !== null && 
          keyword !== undefined && 
          typeof keyword === 'string' && 
          keyword.trim().length > 0
        );
        
        console.log('DataForSeo keywords - Original:', keywordData.keywords.length, 'Valid:', validKeywords.length);
        if (keywordData.keywords.length !== validKeywords.length) {
          console.warn('Invalid keywords filtered from DataForSeo data:', 
            keywordData.keywords.filter(k => !validKeywords.includes(k)));
        }

        if (shouldReplace) {
          setKeywords(validKeywords);
          setSnackbarMessage(
            `Loaded ${validKeywords.length} keywords from DataForSeo analysis` +
            (keywordData.timestamp ? ` (${new Date(keywordData.timestamp).toLocaleString()})` : '')
          );
        } else {
          const newKeywords = validKeywords.filter(k => !keywords.includes(k));
          if (newKeywords.length > 0) {
            setKeywords(prev => [...prev, ...newKeywords]);
            setSnackbarMessage(`Added ${newKeywords.length} new keywords from DataForSeo analysis`);
          } else {
            setSnackbarMessage(`All DataForSeo keywords were already present`);
          }
        }
      } else {
        setSnackbarMessage('No DataForSeo keywords found. Please run DataForSeo analysis first.');
      }
    } catch (error) {
      console.error('Failed to load DataForSeo keywords:', error);
      setSnackbarMessage('Failed to load DataForSeo keywords');
    }
  };

  const handleAddCities = async () => {
    const newCities = cityInput
      .split('\n')
      .map(c => c.trim())
      .filter(c => c && !cities.includes(c));
    
    if (newCities.length > 0) {
      // Combine with existing cities for validation
      const allCities = [...cities, ...newCities];
      setCities(allCities);
      setCityInput('');
      
      // Trigger validation
      await validateCities(allCities);
    }
  };

  const handleAddDomains = () => {
    const newDomains = domainInput
      .split('\n')
      .map(d => d.trim())
      .filter(d => d && !domains.includes(d));
    
    if (newDomains.length > 0) {
      setDomains([...domains, ...newDomains]);
      setDomainInput('');
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleRemoveCity = (index: number) => {
    setCities(cities.filter((_, i) => i !== index));
  };

  const handleRemoveDomain = (index: number) => {
    setDomains(domains.filter((_, i) => i !== index));
  };

  const handleClearAll = () => {
    setKeywords([]);
    setCities([]);
    setDomains([]);
    setKeywordInput('');
    setCityInput('');
    setDomainInput('');
    setResult(null);
    setError(null);
    setRankingSummary(null);
    setSelectedWorkflowResult(null);
    clearValidation();
    // V3 Architecture: No tracking ref to reset
  };

  // City validation handlers
  const handleUpdateCity = (oldCity: string, newCity: string) => {
    const updatedCities = cities.map(c => c === oldCity ? newCity : c);
    setCities(updatedCities);
    // Don't re-validate - just update the local validation results
    if (validationResults && validationResults[newCity]) {
      // Copy the validation result from the new city to replace the old one
      const newValidationResults = { ...validationResults };
      delete newValidationResults[oldCity];
      newValidationResults[newCity] = validationResults[newCity];
      // Note: We can't directly set validationResults since it's from the hook
      // But the UI will update with the new city name
    }
  };

  const handleRemoveCityFromValidator = (city: string) => {
    const updatedCities = cities.filter(c => c !== city);
    setCities(updatedCities);
    // Re-validate remaining cities
    if (updatedCities.length > 0) {
      validateCities(updatedCities);
    } else {
      clearValidation();
      setShowCityValidator(false);
    }
  };

  const handleProceedWithValidation = () => {
    // Filter out invalid cities if needed
    if (validationResults) {
      const validCities = cities.filter(city => validationResults[city]?.valid);
      setCities(validCities);
    }
    setShowCityValidator(false);
  };

  const handleProcessBatch = async () => {
    // Filter out invalid keywords before validation
    const validKeywords = keywords.filter(keyword => 
      keyword !== null && 
      keyword !== undefined && 
      typeof keyword === 'string' && 
      keyword.trim().length > 0
    );
    
    if (validKeywords.length === 0) {
      setError('Please add at least one valid keyword. All current keywords are empty or invalid.');
      return;
    }
    
    if (keywords.length !== validKeywords.length) {
      debug.warning('serp-tool', 'Filtered out invalid keywords', {
        original_count: keywords.length,
        valid_count: validKeywords.length,
        filtered_count: keywords.length - validKeywords.length
      }, 'SerpTool');
    }

    // Recalculate total queries with valid keywords
    const validTotalQueries = validKeywords.length * (cities.length || 1);
    
    if (validTotalQueries > 5000) {
      setError(`Too many queries (${validTotalQueries}). Maximum allowed is 5000.`);
      return;
    }

    setLoading(true);
    setError(null);
    setProgress(0);
    setRankingSummary(null);
    setCurrentQuery('');
    setProcessedCount(0);
    setTotalCount(0);
    
    // Reset previous results
    setResult(null);

    // V3 Architecture: No Snowflake state to reset - integration is automatic

    try {
      // Use canonical names for cities if available from validation
      const citiesToSend = cities.length > 0 ? cities.map(city => {
        const validation = validationResults?.[city];
        // Use canonical name if available and valid, otherwise use original city
        if (validation?.valid && validation.canonical_name) {
          console.log(`Using canonical name for "${city}": "${validation.canonical_name}"`);
          return validation.canonical_name;
        } else {
          console.log(`Using original city name: "${city}" (no validation or invalid)`);
          return city;
        }
      }) : undefined;

      // Filter out any null, undefined, or empty keywords before sending
      const cleanKeywords = keywords.filter(keyword => 
        keyword !== null && 
        keyword !== undefined && 
        typeof keyword === 'string' && 
        keyword.trim().length > 0
      );

      console.log('Original keywords count:', keywords.length);
      console.log('Clean keywords count:', cleanKeywords.length);
      if (keywords.length !== cleanKeywords.length) {
        console.warn('Filtered out invalid keywords:', keywords.filter(k => !cleanKeywords.includes(k)));
      }

      const requestBody = {
        keywords: cleanKeywords,
        cities: citiesToSend,
        google_domain: googleDomain,
        api_key: apiKey || undefined,
        rate_limit_ms: rateLimitMs,
        num_results: numResults,
        domains: domains.length > 0 ? domains : undefined,
        client_id: isWorkflowMode ? clientId : undefined,
        workflow_id: isWorkflowMode ? workflowId : undefined,
      };

      debug.info('Sending SERP batch request', {
        endpoint: '/api/v1/serp/batch',
        total_queries: validTotalQueries,
        request_summary: {
          keywords_count: cleanKeywords.length,
          cities_count: citiesToSend?.length || 0,
          domains_count: domains.length,
          google_domain: googleDomain,
          num_results: numResults,
          rate_limit_ms: rateLimitMs
        }
      }, 'SerpTool');

      // Step 1: Start the batch processing
      const startResponse = await callApi<SerpBatchStartResponse>('/api/v1/serp/batch', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (!startResponse) {
        debug.error('SERP batch API returned null response', {}, 'SerpTool');
        throw new Error('Failed to start batch processing');
      }

      const currentBatchId = startResponse.batch_id;
      setBatchId(currentBatchId); // Store for resume functionality
      
      // Log architecture version detection
      if (startResponse.architecture_version === 'v2') {
        debug.info('V2 architecture detected in SERP response', {
          batch_id: currentBatchId,
          architecture_version: startResponse.architecture_version,
          blob_first: startResponse.blob_first,
          snowflake_ingestion_ready: !!startResponse.snowflake_ingestion
        }, 'SerpTool');
      }
      
      debug.info('SERP batch processing started successfully', {
        batch_id: currentBatchId,
        total_queries: validTotalQueries,
        architecture_version: startResponse.architecture_version || 'v1',
        max_queries: startResponse.max_queries
      }, 'SerpTool');
      setSnackbarMessage(`Batch processing started. Batch ID: ${currentBatchId}`);
      
      // Trigger stepper refresh to show processing state immediately
      if (isWorkflowMode && triggerRefresh) {
        triggerRefresh();
        console.log('Triggered WorkflowStepper refresh after SERP batch start');
      }
      
      // Step 2: Start polling using the reusable function
      startPollingForBatch(currentBatchId, totalQueries, rateLimitMs);

    } catch (err: any) {
      debug.error('SERP batch processing failed', {
        error_message: err.message,
        error_stack: err.stack,
        request_details: {
          keywords_count: validKeywords.length,
          cities_count: cities.length,
          domains_count: domains.length,
          total_queries: validTotalQueries
        }
      }, 'SerpTool');
      
      setLoading(false); // Stop loading on initialization error
      setError(err.message || 'Failed to process SERP batch');
      setProgress(0);
      
      // Trigger stepper refresh to show failed state
      if (isWorkflowMode && triggerRefresh) {
        triggerRefresh();
      }
    }
  };

  const fetchSummaryReport = async (summaryUrl: string) => {
    try {
      const response = await fetch(summaryUrl);
      if (response.ok) {
        const summary = await response.json();
        setRankingSummary(summary);
      }
    } catch (err) {
      console.error('Failed to fetch summary report:', err);
    }
  };

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setSnackbarMessage(`Downloading ${filename}...`);
  };

  // Function to navigate to interactive report
  const handleViewInteractiveReport = () => {
    if (result) {
      navigate(`/interactive-report/${result.batch_id}`, {
        state: { 
          rankingSummary: rankingSummary || null,
          resultData: result 
        }
      });
    }
  };

  return (
    <Box className="container mx-auto py-6">
      {/* Main layout with stepper */}
      <Grid container spacing={2} columnGap={!isStepperCollapsed ? 4 : 0}>
        {/* WorkflowStepper - only show in workflow mode */}
        {isWorkflowMode && (
          <Grid item xs="auto">
            <WorkflowStepper
              activeStep={5} // SERP Analysis is step 5 (0-indexed)
              workflowId={workflowId || ''}
              clientId={clientId}
              clientName={clientData?.name}
              moduleCompletionStatus={getWorkflowStatus(workflowId)}
              onCollapseChange={handleStepperCollapseChange}
            />
          </Grid>
        )}
        
        {/* Main content area */}
        <Grid 
          item 
          xs
          sx={{
            pl: isWorkflowMode && !isStepperCollapsed ? 3 : 0,
            position: 'relative'
          }}
        >
          <Box>
            {/* Breadcrumb navigation with back button */}
            {isWorkflowMode && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Breadcrumbs 
                  aria-label="breadcrumb" 
                  sx={{ '& .MuiBreadcrumbs-separator': { color: 'text.secondary' } }}
                >
                  <Link 
                    color="inherit" 
                    href="/" 
                    onClick={(e) => { e.preventDefault(); navigate('/'); }}
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                    Dashboard
                  </Link>
                  <Typography color="text.secondary">
                    {clientData?.name || `Client ${clientId}`} ({clientId} - {workflowId})
                  </Typography>
                  <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <TrendingUpIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    SERP Analysis
                  </Typography>
                </Breadcrumbs>
                
                <MuiButton
                  variant="outlined"
                  size="small"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/start-process')}
                  sx={{ ml: 2 }}
                >
                  Start Process
                </MuiButton>
              </Box>
            )}

      {/* Workflow Result Selection or Snowflake Sampling */}
      <Box sx={{ mb: 3 }}>
        {isWorkflowMode ? (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <InsightsIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Snowflake Keyword Sampling
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Load keywords from Snowflake with intelligent sampling to optimize SERP API costs.
              </Typography>
              <MuiButton
                variant="contained"
                startIcon={<CloudIcon />}
                onClick={() => setShowSnowflakeSampling(true)}
                disabled={loadingSnowflakeKeywords}
              >
                {loadingSnowflakeKeywords ? 'Loading...' : 'Configure Unified Sampling'}
              </MuiButton>
            </CardContent>
          </Card>
        ) : null}        
      </Box>

      <Grid container spacing={3}>
        {/* Keywords Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Keywords *
              </Typography>
              <TextField
                multiline
                rows={4}
                fullWidth
                placeholder="Enter keywords (one per line)"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <MuiButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddKeywords}
                  disabled={!keywordInput.trim()}
                  sx={{ flex: 1 }}
                >
                  Add Keywords
                </MuiButton>
              <MuiButton
                variant="outlined"
                startIcon={<TrendingUpIcon />}
                onClick={handleLoadDataForSeoKeywords}
                sx={{ flex: 1 }}
                title="Load keywords from a previously completed DataForSeo analysis"
              >
                Load from DataForSeo
              </MuiButton>
              {isWorkflowMode && (
                <MuiButton
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={checkForExistingBatch}
                  sx={{ flex: 1 }}
                  title="Check for existing SERP batch in this workflow"
                >
                  Check Running Batch
                </MuiButton>
              )}
              </Box>
              
              {keywords.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2">
                      Keywords ({keywords.length}):
                    </Typography>
                    {selectedWorkflowResult && (
                      <Chip
                        label={`From: ${selectedWorkflowResult.workflow_name}`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto' }}>
                    <List dense>
                      {keywords.map((keyword, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={keyword} />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveKeyword(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right Column - Cities and Domains */}
        <Grid item xs={12} md={6}>
          {/* Cities Section - Now Collapsible */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationCityIcon color="action" />
                  <Typography variant="h6">
                    Cities (Optional)
                  </Typography>
                  {cities.length > 0 && (
                    <Chip 
                      label={cities.length} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                </Box>
                <IconButton 
                  onClick={() => setCitiesExpanded(!citiesExpanded)}
                  size="small"
                >
                  {citiesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add cities to search from specific locations. Cities are used as geo-location parameters, 
                not appended to keywords. Validated cities will use canonical names from SERP API for accurate targeting.
              </Typography>

              <Collapse in={citiesExpanded}>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Enter cities (one per line)"
                  value={cityInput}
                  onChange={(e) => setCityInput(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <MuiButton
                  variant="contained"
                  startIcon={isValidating ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                  onClick={handleAddCities}
                  disabled={!cityInput.trim() || isValidating}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {isValidating ? 'Validating Cities...' : 'Add Cities'}
                </MuiButton>
                
                {cities.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">
                        Cities ({cities.length}):
                      </Typography>
                      {validationSummary && (
                        <Chip
                          label={`${validationSummary.valid_cities} valid, ${validationSummary.invalid_cities} invalid`}
                          size="small"
                          color={validationSummary.invalid_cities > 0 ? 'warning' : 'success'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto', position: 'relative' }}>
                      {isValidating && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 1,
                          }}
                        >
                          <CircularProgress size={30} />
                        </Box>
                      )}
                      <List dense>
                        {cities.map((city, index) => {
                          const validation = validationResults?.[city];
                          return (
                            <ListItem key={index}>
                              <ListItemIcon sx={{ minWidth: 30 }}>
                                {validation ? (
                                  validation.valid ? (
                                    <CheckCircleIcon fontSize="small" color="success" />
                                  ) : (
                                    <ErrorIcon fontSize="small" color="error" />
                                  )
                                ) : isValidating ? (
                                  <CircularProgress size={16} />
                                ) : null}
                              </ListItemIcon>
                              <ListItemText 
                                primary={city}
                                secondary={
                                  validation ? (
                                    validation.valid ? 
                                      `✓ Will use: ${validation.canonical_name}` : 
                                      validation.error
                                  ) : 'Not validated'
                                }
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleRemoveCity(index)}
                                  disabled={isValidating}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                    <MuiButton
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        validateCities(cities);
                        setShowCityValidator(true);
                      }}
                      sx={{ mt: 1 }}
                      fullWidth
                      disabled={isValidating}
                      startIcon={isValidating ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {isValidating ? 'Validating...' : 'Validate Cities'}
                    </MuiButton>
                  </Box>
                )}
              </Collapse>

              {!citiesExpanded && cities.length === 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Click to expand and add cities for keyword-city combinations
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Domains Section - Now Collapsible */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon color="action" />
                  <Typography variant="h6">
                    Domains (Optional)
                  </Typography>
                  {domains.length > 0 && (
                    <Chip 
                      label={domains.length} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  )}
                </Box>
                <IconButton 
                  onClick={() => setDomainsExpanded(!domainsExpanded)}
                  size="small"
                >
                  {domainsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add domains to filter results. If no domains are added, all results will be processed.
              </Typography>

              <Collapse in={domainsExpanded}>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Enter domains (one per line)"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <MuiButton
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddDomains}
                  disabled={!domainInput.trim()}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Add Domains
                </MuiButton>
                
                {domains.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Domains ({domains.length}):
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto' }}>
                      <List dense>
                        {domains.map((domain, index) => (
                          <ListItem key={index}>
                            <ListItemText primary={domain} />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleRemoveDomain(index)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Box>
                )}
              </Collapse>

              {!domainsExpanded && domains.length === 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Click to expand and add domains for result filtering
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Configuration Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configuration
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Google Domain</InputLabel>
                    <Select
                      value={googleDomain}
                      onChange={(e) => setGoogleDomain(e.target.value)}
                      label="Google Domain"
                    >
                      {GOOGLE_DOMAINS.map((domain) => (
                        <MenuItem key={domain.value} value={domain.value}>
                          {domain.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Results per Query</InputLabel>
                    <Select
                      value={numResults}
                      onChange={(e) => setNumResults(Number(e.target.value))}
                      label="Results per Query"
                    >
                      {RESULT_COUNT_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Rate Limit (ms)"
                    type="number"
                    value={rateLimitMs}
                    onChange={(e) => setRateLimitMs(Number(e.target.value))}
                    inputProps={{ min: 100, max: 10000, step: 100 }}
                    helperText="Delay between API calls"
                  />
                </Grid>
                
                <Grid item xs={12} md={3}>
                  <MuiButton
                    fullWidth
                    variant="outlined"
                    onClick={() => setShowApiKeyDialog(true)}
                    sx={{ height: '56px' }}
                  >
                    {apiKey ? 'API Key Set' : 'Set SerpAPI Key (Optional)'}
                  </MuiButton>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Query Summary */}
        <Grid item xs={12}>
          <Card sx={{ bgcolor: queryLimitExceeded ? 'error.light' : 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Query Summary
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Typography variant="body1">
                    Total Queries: <strong>{totalQueries}</strong>
                    {cities.length > 0 
                      ? ` (${keywords.length} keywords × ${cities.length} cities)`
                      : ` (${keywords.length} keywords only)`
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Results per query: {numResults} | 
                    {domains.length > 0 ? ` Tracking ${domains.length} domains` : ' No domain tracking'}
                  </Typography>
                  {queryLimitExceeded && (
                    <Typography variant="body2" color="error">
                      Maximum 5000 queries allowed. Please reduce keywords or cities.
                    </Typography>
                  )}
                  {totalQueries > 0 && !queryLimitExceeded && (
                    <Typography variant="body2" color="text.secondary">
                      Estimated time: ~{Math.ceil(totalQueries * rateLimitMs / 1000)} seconds
                    </Typography>
                  )}
                  {cities.length === 0 && keywords.length > 0 && (
                    <Typography variant="body2" color="info.main" sx={{ mt: 1 }}>
                      Running keywords without city combinations
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={5}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <MuiButton
                      variant="outlined"
                      color="error"
                      startIcon={<ClearIcon />}
                      onClick={handleClearAll}
                      disabled={loading}
                      sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                    >
                      Clear All
                    </MuiButton>
                    <MuiButton
                      variant="contained"
                      startIcon={<SearchIcon />}
                      onClick={handleProcessBatch}
                      disabled={loading || keywords.length === 0 || queryLimitExceeded}
                      sx={{ flex: 1, whiteSpace: 'nowrap', minWidth: 140 }}
                    >
                      Process Batch
                    </MuiButton>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Progress */}
        {loading && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Processing SERP Batch{batchId ? ` - ${batchId}` : ''}
                </Typography>
                <LinearProgress variant={progress === 100 ? "indeterminate" : "determinate"} value={progress} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {progress === 100 ? "Finalizing results..." : `${Math.round(progress)}% Complete`}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalCount > 0 ? `${processedCount}/${totalCount} queries` : `${totalQueries} total queries`}
                  </Typography>
                </Box>
                {progress === 100 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Uploading results and generating reports...
                    </Typography>
                  </Box>
                ) : currentQuery ? (
                  <Typography variant="body2" color="text.primary" sx={{ 
                    fontStyle: 'italic', 
                    mt: 1, 
                    p: 1, 
                    bgcolor: 'grey.50', 
                    borderRadius: 1,
                    fontSize: '0.875rem'
                  }}>
                    Currently processing: {currentQuery}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Initializing batch processing...
                  </Typography>
                )}
                
                {/* Resume Polling Button - shown if polling has stopped but batch might still be processing */}
                {!pollingIntervalRef.current && batchId && (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <MuiButton 
                      variant="outlined" 
                      size="small" 
                      onClick={() => startPollingForBatch(batchId, totalQueries, rateLimitMs)}
                    >
                      Resume Progress Tracking
                    </MuiButton>
                    <MuiButton 
                      variant="outlined" 
                      size="small" 
                      onClick={async () => {
                        try {
                          const statusResponse = await callApi<SerpBatchStatusResponse>(`/api/v1/serp/batch/${batchId}/status`);
                          if (statusResponse) {
                            console.log('Current batch status:', statusResponse);
                            alert(`Batch Status: ${statusResponse.status}\nProgress: ${statusResponse.progress_percentage || 0}%\nCompleted: ${statusResponse.completed_queries || 0}/${statusResponse.total_queries || 0}`);
                          }
                        } catch (err) {
                          console.error('Error checking status:', err);
                        }
                      }}
                    >
                      Check Status
                    </MuiButton>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* V3 Architecture: Snowflake integration now handled automatically during SERP processing */}
        {/* No separate Snowflake progress bar needed */}

        {/* Ranking Summary */}
        {rankingSummary && !loading && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <AssessmentIcon color="primary" />
                  <Typography variant="h6">
                    Ranking Summary Report
                  </Typography>
                </Box>
                
                {/* Overview Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Queries with Rankings
                    </Typography>
                    <Typography variant="h6">
                      {rankingSummary.overview.queries_with_rankings}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total Rankings Found
                    </Typography>
                    <Typography variant="h6">
                      {rankingSummary.overview.total_rankings_found}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Average Position
                    </Typography>
                    <Typography variant="h6">
                      {rankingSummary.overview.average_position || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Best Ranking
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {rankingSummary.overview.best_ranking || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Domain Summary Table */}
                {Object.keys(rankingSummary.domain_summary).length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6">Domain Performance Summary</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Domain</TableCell>
                              <TableCell align="center">Appearances</TableCell>
                              <TableCell align="center">Avg Position</TableCell>
                              <TableCell align="center">Best</TableCell>
                              <TableCell align="center">Worst</TableCell>
                              <TableCell align="center">Organic</TableCell>
                              <TableCell align="center">Local</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(rankingSummary.domain_summary).map(([domain, stats]) => (
                              <TableRow key={domain}>
                                <TableCell>{domain}</TableCell>
                                <TableCell align="center">{stats.total_appearances}</TableCell>
                                <TableCell align="center">
                                  {stats.average_position || 'N/A'}
                                </TableCell>
                                <TableCell align="center" sx={{ color: 'success.main' }}>
                                  {stats.best_position || 'N/A'}
                                </TableCell>
                                <TableCell align="center" sx={{ color: 'warning.main' }}>
                                  {stats.worst_position || 'N/A'}
                                </TableCell>
                                <TableCell align="center">{stats.result_types.organic}</TableCell>
                                <TableCell align="center">{stats.result_types.local}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                <MuiButton
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => setShowSummaryDialog(true)}
                  sx={{ mt: 2 }}
                >
                  View Detailed Rankings
                </MuiButton>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Results */}
        {result && !loading && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">
                    Download Results
                  </Typography>
                  
                  {/* Snowflake Integration Status - now handled by backend */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudIcon 
                      color={result?.storage_info?.snowflake_save_success ? 'success' : 
                             result?.storage_info?.snowflake_save_error ? 'error' : 'action'} 
                      fontSize="small" 
                    />
                    <Typography 
                      variant="body2" 
                      color={result?.storage_info?.snowflake_save_success ? 'success.main' : 
                             result?.storage_info?.snowflake_save_error ? 'error.main' : 'text.secondary'}
                    >
                      {result?.storage_info?.snowflake_save_success ? 'Saved to Snowflake' : 
                       result?.storage_info?.snowflake_save_error ? 'Snowflake save failed' : 'Snowflake integration'}
                    </Typography>
                  </Box>
                </Box>

                {/* Snowflake Status Alert - now handled by backend */}
                {result?.storage_info?.snowflake_save_error && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Snowflake Save Failed:</strong> {result.storage_info.snowflake_save_error}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      Results are still available for download. Snowflake integration is optional.
                    </Typography>
                  </Alert>
                )}

                {result?.storage_info?.snowflake_save_success && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>✅ Saved to Snowflake:</strong> SERP results have been automatically saved to Snowflake database for future analysis.
                    </Typography>
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Batch ID
                    </Typography>
                    <Typography variant="body1">
                      {result.batch_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Success Rate
                    </Typography>
                    <Typography variant="body1">
                      {result.success_count}/{result.total_queries} 
                      {' '}({Math.round((result.success_count / result.total_queries) * 100)}%)
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Failed Queries
                    </Typography>
                    <Typography variant="body1" color={result.failed_count > 0 ? 'error' : 'text.primary'}>
                      {result.failed_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Processing Time
                    </Typography>
                    <Typography variant="body1">
                      {result.processing_time_seconds ? result.processing_time_seconds.toFixed(1) : '0.0'}s
                    </Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {/* Hide download buttons for V2 architecture - no more ZIP/JSON files */}
                  {result.download_urls?.single_file && (
                    <MuiButton
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(result.download_urls.single_file, `serp_results_${result.batch_id}.json`)}
                    >
                      Download JSON
                    </MuiButton>
                  )}
                  {result.download_urls?.zip_file && (
                    <MuiButton
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownload(result.download_urls.zip_file, `serp_results_${result.batch_id}.zip`)}
                    >
                      Download ZIP
                    </MuiButton>
                  )}
                  {result.has_domains && result.download_urls?.summary_report && (
                    <MuiButton
                      variant="contained"
                      color="secondary"
                      startIcon={<AssessmentIcon />}
                      onClick={() => handleDownload(result.download_urls.summary_report!, `ranking_summary_${result.batch_id}.json`)}
                    >
                      Download Summary Report
                    </MuiButton>
                  )}
                  <MuiButton
                    variant="contained"
                    color="info"
                    startIcon={<DashboardIcon />}
                    onClick={handleViewInteractiveReport}
                  >
                    View Interactive Report
                  </MuiButton>
                  
                  {/* Workflow navigation button */}
                  {isWorkflowMode && (
                    <MuiButton
                      variant="contained"
                      color="primary"
                      startIcon={<NavigateNextIcon />}
                      onClick={() => navigate(`/data-for-seo-tool/client/${clientId}/${workflowId}`)}
                      sx={{ ml: 'auto' }}
                    >
                      Continue to DataForSEO
                    </MuiButton>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />

      {/* Summary Dialog */}
      <Dialog
        open={showSummaryDialog}
        onClose={() => setShowSummaryDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Detailed Ranking Summary</DialogTitle>
        <DialogContent>
          {rankingSummary && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Query Rankings
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Query</TableCell>
                      <TableCell align="center">Organic Results</TableCell>
                      <TableCell align="center">Local Results</TableCell>
                      <TableCell>Domain Rankings</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rankingSummary.query_rankings.map((query, index) => (
                      <TableRow key={index}>
                        <TableCell>{query.query}</TableCell>
                        <TableCell align="center">{query.organic_results_count}</TableCell>
                        <TableCell align="center">{query.local_results_count}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {Object.entries(query.domain_rankings).map(([domain, ranking]) => (
                              <Chip
                                key={domain}
                                label={`${domain}: #${ranking.position}`}
                                size="small"
                                color={ranking.position <= 3 ? 'success' : ranking.position <= 10 ? 'warning' : 'default'}
                              />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setShowSummaryDialog(false)}>Close</MuiButton>
        </DialogActions>
      </Dialog>

      {/* API Key Dialog */}
      <Dialog open={showApiKeyDialog} onClose={() => setShowApiKeyDialog(false)}>
        <DialogTitle>Set SerpAPI Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="SerpAPI Key"
            type="password"
            fullWidth
            variant="outlined"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            helperText="Optional: Provide your SerpAPI key for higher rate limits"
          />
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setShowApiKeyDialog(false)}>Cancel</MuiButton>
          <MuiButton onClick={() => setShowApiKeyDialog(false)} variant="contained">
            Save
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* City Validator Dialog */}
      <SerpCityValidator
        open={showCityValidator}
        onClose={() => setShowCityValidator(false)}
        cities={cities}
        validationResults={validationResults}
        isValidating={isValidating}
        onUpdateCity={handleUpdateCity}
        onRemoveCity={handleRemoveCityFromValidator}
        onProceed={handleProceedWithValidation}
      />
      
      {/* Unified Snowflake Sampling Dialog */}
      {isWorkflowMode && clientId && workflowId && (
        <UnifiedSamplingDialog
          open={showSnowflakeSampling}
          onClose={() => setShowSnowflakeSampling(false)}
          clientId={clientId}
          workflowId={workflowId}
          onKeywordsLoaded={handleSnowflakeKeywordsLoaded}
        />
      )}
              </Box>
            </Grid>
                </Grid>
    </Box>
  );
};

export default SerpTool;